import {roomEventApi} from '@/core/api/room.event.api'

import {Device} from "@/utils";
import {WebPubSubClient} from '@azure/web-pubsub-client'
import {Room} from "@/core/api/room.api";
import {Http} from "@/core/service/http.init";
import {ErrorWrapper} from '@/core/service/util'

class GrmRoomWebPubsubClient {
    #connectionId;
    #isReconnected = false;
    #isClosed = false;
    #isBackground = false;
    #closeReason = "going_away";

    #roomId;
    #userId;
    #roomUserUuid;
    #joinedGroups = new Set();

    #webPubsubClient;

    #enableAutoReconnect = true;
    #reconnectMaxCount = 20;
    #reconnectBaseDelayMs = 1 * 1000;
    #reconnectMaxDelayMs = 3 * 1000;

    // for ping
    #pingIntervalMs;
    #pingTimer = null;

    // for background close timer
    #backgroundCloseTimer = null;
    #backgroundTimeoutMs = 180 * 1000;

    constructor() {}

    async connect(data) {
        this.#roomId = data.roomUser.roomId;
        this.#userId = data.roomUser.userId;
        this.#roomUserUuid = data.roomUserUuid;

        const connection = await this._getConnection();
        const connectionUrl = connection.connectionUrl;
        const connectionGroups = connection.connectionGroups;
        const connectionIntervalMinutes = connection.connectionIntervalMinutes;
        const pingIntervalMinutes = !!connectionIntervalMinutes && connectionIntervalMinutes > 0 ? connectionIntervalMinutes : 1;
        this.#pingIntervalMs = pingIntervalMinutes * 60 * 1000;

        this.#webPubsubClient = await this._createWebPubsubClient(connectionUrl);
        // this.#webPubsubClient._activeTimeoutInMs = 3 * 1000;
        this._addEventHandler();
        await this.#webPubsubClient.start();
        await this._joinGroups(connectionGroups);
        this._startPing();
    }

    async joinGroup(group) {
        await this._joinGroup(group);
    }

    async leaveGroup(group) {
        await this._leaveGroup(group);
    }

    async _close(reason) {
        this.#isClosed = true;
        this.#closeReason = reason;
        this._stopPing();
        await this.#webPubsubClient.stop();
    }

    async _getConnection() {
        const response = await Http.getHttpInstance({auth: true})
        .post(`/ws/room/${this.#roomId}/connection?roomUserUuid=${this.#roomUserUuid}`)
        .catch(e => {
            console.error('## [GrmRoomWebPubSub] connection failed', e);
            throw new ErrorWrapper(e)
        });
        return response.data.data;
    }

    async _createWebPubsubClient(connectionUrl) {
        return new WebPubSubClient(connectionUrl, {
            autoReconnect: this.#enableAutoReconnect,
            reconnectRetryOptions: {
                mode: "Exponential",
                maxRetries: this.#reconnectMaxCount,
                retryDelayInMs: this.#reconnectBaseDelayMs,
                maxRetryDelayInMs: this.#reconnectMaxDelayMs
            }
        });
    }

    _addEventHandler() {
        this.#webPubsubClient.on("connected", async (event) => {
            console.debug("WPS connected : ", event);
            console.debug("WPS client : ", this.#webPubsubClient);
            this.#connectionId = event.connectionId;
            if(this.#isReconnected) {
                await roomEventApi.trigger("room_reconnected");
            }
            this.#isClosed = false;
            this.#isReconnected = false;
        });

        this.#webPubsubClient.on("server-message", (event) => {
            try {
                console.debug("WPS received server-message : ", event);
            } catch (e) {
                console.error("WPS receive server message error", e);
            }
        });

        this.#webPubsubClient.on("group-message", async (event) => {
            try {
                console.debug("WPS received group-message : ", event.message.data);
                const message = event.message.data;
                await roomEventApi.pushEvent(message);
            } catch (e) {
                console.error("WPS receive group message error", e);
            }
        });

        this.#webPubsubClient.on("rejoin-group-failed", async (event) => {
            try {
                console.debug("WPS rejoin group failed", event);
                if(this._isClosedOrLeaved()) {
                    return;
                }
                await this._joinGroups(this.#joinedGroups);
            } catch (e) {
                console.error("WPS rejoin group error", e);
                await this._close("room_disconnected");
            }
        });

        this.#webPubsubClient.on("disconnected", async (event) => {
            try {
                console.debug("WPS disconnected : ", event);
                // this._stopPing();
                if(this._isClosedOrLeaved()) {
                    return;
                }
                this.#isReconnected = true;

                const error = event.message;
                if(!!error && error.type === 'system') {
                    if(!!error.message && error.message.indexOf('Reason:') > -1) {
                        console.warn("WPS disconnected system err messsage", error.message);
                        const reason = error.message.substring(error.message.indexOf('Reason:') + 7);
                        await this._close(reason);
                        return;
                    }
                    await this._close('room_disconnected');
                    return;
                }

                await roomEventApi.trigger("room_disconnected", { retry: true });

                const isExistsConnectionResponse = await Http.getHttpInstance({ auth: false }).get(`/ws/room/connection/${this.#connectionId}/exists`);
                const isExistsConnectionBody = isExistsConnectionResponse.data.data;
                const isExistsConnection = isExistsConnectionBody.isExists;
                if (!isExistsConnection) {
                    // await this._close("room_disconnected");
                    await roomEventApi.trigger("room_disconnected", { retry: false });
                }
            } catch (e) {
                console.error("WPS disconnected error", e);
                await this._close("room_disconnected");
            }
        });

        this.#webPubsubClient.on("stopped", async () => {
            try {
                console.debug(`WPS stopped, reason : ${this.#closeReason}`);
                this._stopPing();
                // await Room.leaved({ reason : this.#closeReason });
                await roomEventApi.trigger("room_disconnected", { retry: false });
            } catch (e) {
                console.error("WPS stopped error", e);
            } finally {
                this.#isReconnected = true;
            }
        });
    }

    _isClosedOrLeaved() {
        return this.#isClosed || Room.isLeaved();
    }

    async _joinGroups(groups) {
        for (const group of groups) {
            await this._joinGroup(group);
        }
    }

    async _joinGroup(group) {
        try {
            await this.#webPubsubClient.joinGroup(group);
            this._addJoinedGroup(group);
        } catch (e) {
            console.error("WPS join group error", e);
        }
    }

    _addJoinedGroup(group) {
        if (this.#joinedGroups.has(group)) {
            return;
        }
        this.#joinedGroups.add(group);
    }

    async _leaveGroup(group) {
        try {
            await this.#webPubsubClient.leaveGroup(group);
            this.#joinedGroups.delete(group);
        } catch (e) {
            console.error(e);
        }
    }

    // ping send
    async _sendPing() {
        if (this._isClosedOrLeaved() || !this.#connectionId) {
            return;
        }

        try {
            console.debug(`WPS send ping connectionId: ${this.#connectionId}`);
            Http.getHttpInstance({ auth: false })
            .post(`/ws/room/${this.#roomId}/connection/${this.#connectionId}/ping`,
                {},
                { headers: {'ce-userId': this.#userId} })
            .catch(e => {
                console.error("WPS send ping failed:", e);
            });
            this._schedulePing();
        } catch (e) {
            console.error("WPS send ping error:", e);
            this._schedulePing();
        }

        /*
        // @Deprecated (Azure custom event)
        try {
            console.debug(`WPS send ping connectionId: ${this.#connectionId}`);
            this.#webPubsubClient.sendEvent("ping", {}, "json");
        } catch (error) {
            console.error("WPS send ping failed:", error);
        }
        */
    }

    // ping start
    _startPing() {
        this._stopPing();
        this._sendPing();
    }

    // ping next
    _schedulePing() {
        this._stopPing();
        this.#pingTimer = setTimeout(() => {
            this._sendPing();
        }, this.#pingIntervalMs);
    }

    // ping stop
    _stopPing() {
        if (this.#pingTimer) {
            clearTimeout(this.#pingTimer);
            this.#pingTimer = null;
        }
    }

    // @Deprecated
    toForegroundMode() {
        if(!Device.isMobile() && !Device.isTablet()){
            return;
        }

        if(this._isClosedOrLeaved()) {
            roomEventApi.trigger("room_disconnected", { retry: false });
        }

        this.#isBackground = false;

        // cancel background timer
        if (this.#backgroundCloseTimer) {
            clearTimeout(this.#backgroundCloseTimer);
            this.#backgroundCloseTimer = null;
            console.debug("WPS background close timer canceled");
        }
    }

    // @Deprecated
    toBackgroundMode() {
        if(!Device.isMobile() && !Device.isTablet()){
            return;
        }

        this.#isBackground = true;

        if (this.#isClosed) {
            return;
        }

        // set background close timer
        if (this.#backgroundCloseTimer) {
            clearTimeout(this.#backgroundCloseTimer);
        }

        console.debug(`WPS setting background close timeout: connection will close after ${this.#backgroundTimeoutMs/1000} seconds`);
        this.#backgroundCloseTimer = setTimeout(async () => {
            if (this.#isBackground && !this.#isClosed) {
                console.debug("WPS closing connection background close timeout");
                await this._close("background_disconnected");
            }
        }, this.#backgroundTimeoutMs);
    }
}

export const grmRoomWebPubsubClient = new GrmRoomWebPubsubClient();